import React, { useMemo } from "react";
import { MultiSelectOption } from "@components/multiselect2/MultiSelect2";
import { Button } from "@components/ui/button";
import { Badge } from "@components/ui/badge";
import { Separator } from "@components/ui/separator";
import { X } from "lucide-react";
import {
  SelectionCombobox,
  SelectionComboboxOption,
} from "@components/ui/selection-combobox";

interface FilterPanelProps {
  customerOptions: MultiSelectOption[];
  includedCustomers: string[];
  excludedCustomers: string[];
  onChange: (included: string[], excluded: string[]) => void;
  onClose?: () => void;
}

const dedupeSelectionOptions = (options: SelectionComboboxOption[]) =>
  Array.from(new Map(options.map((option) => [option.value, option])).values());

export const FilterPanel: React.FC<FilterPanelProps> = ({
  customerOptions,
  includedCustomers,
  excludedCustomers,
  onChange,
  onClose,
}) => {
  const customerMap = useMemo(() => {
    return new Map(
      customerOptions.map((option) => [option.value, option.label])
    );
  }, [customerOptions]);

  const availableIncludeOptions = useMemo<SelectionComboboxOption[]>(() => {
    const includedSet = new Set(includedCustomers);
    return dedupeSelectionOptions(
      customerOptions
      .filter((option) => !includedSet.has(option.value))
      .map((option) => ({ value: option.value, label: option.label }))
    );
  }, [customerOptions, includedCustomers]);

  const availableExcludeOptions = useMemo<SelectionComboboxOption[]>(() => {
    const excludedSet = new Set(excludedCustomers);
    return dedupeSelectionOptions(
      customerOptions
      .filter((option) => !excludedSet.has(option.value))
      .map((option) => ({ value: option.value, label: option.label }))
    );
  }, [customerOptions, excludedCustomers]);

  const handleIncludeSelect = (customerId: string) => {
    const nextIncluded = Array.from(
      new Set([...includedCustomers, customerId])
    );
    const nextExcluded = excludedCustomers.filter((id) => id !== customerId);
    onChange(nextIncluded, nextExcluded);
  };

  const handleExcludeSelect = (customerId: string) => {
    const nextExcluded = Array.from(
      new Set([...excludedCustomers, customerId])
    );
    const nextIncluded = includedCustomers.filter((id) => id !== customerId);
    onChange(nextIncluded, nextExcluded);
  };

  const handleRemoveIncluded = (customerId: string) => {
    const nextIncluded = includedCustomers.filter((id) => id !== customerId);
    onChange(nextIncluded, excludedCustomers);
  };

  const handleRemoveExcluded = (customerId: string) => {
    const nextExcluded = excludedCustomers.filter((id) => id !== customerId);
    onChange(includedCustomers, nextExcluded);
  };

  const handleClear = () => {
    onChange([], []);
    onClose?.();
  };

  return (
    <div className="w-full max-w-sm">
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold">Customer filters</h3>
            <p className="text-xs text-muted-foreground">
              Refine the organisations included in dashboard metrics.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={handleClear}>
            Clear
          </Button>
        </div>

        <div className="space-y-3">
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Include customers
            </p>
            <SelectionCombobox
              placeholder="Add customer..."
              options={availableIncludeOptions}
              emptyMessage="No customers available"
              onSelect={handleIncludeSelect}
              triggerClassName="h-8"
              contentClassName="w-[260px]"
            />
            <div className="flex flex-wrap gap-2">
              {includedCustomers.length > 0 ? (
                includedCustomers.map((customerId) => {
                  const label = customerMap.get(customerId) ?? customerId;
                  return (
                    <Badge
                      key={`included-${customerId}`}
                      variant="secondary"
                      className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
                    >
                      <span className="max-w-[160px] truncate">{label}</span>
                      <button
                        type="button"
                        className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => handleRemoveIncluded(customerId)}
                        aria-label={`Remove ${label} from included customers`}
                      >
                        <X className="size-3" aria-hidden="true" />
                      </button>
                    </Badge>
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground">
                  No customers currently included.
                </p>
              )}
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Exclude customers
            </p>
            <SelectionCombobox
              placeholder="Exclude customer..."
              options={availableExcludeOptions}
              emptyMessage="No customers available"
              onSelect={handleExcludeSelect}
              triggerClassName="h-8"
              contentClassName="w-[260px]"
            />
            <div className="flex flex-wrap gap-2">
              {excludedCustomers.length > 0 ? (
                excludedCustomers.map((customerId) => {
                  const label = customerMap.get(customerId) ?? customerId;
                  return (
                    <Badge
                      key={`excluded-${customerId}`}
                      variant="secondary"
                      className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
                    >
                      <span className="max-w-[160px] truncate">{label}</span>
                      <button
                        type="button"
                        className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => handleRemoveExcluded(customerId)}
                        aria-label={`Remove ${label} from excluded customers`}
                      >
                        <X className="size-3" aria-hidden="true" />
                      </button>
                    </Badge>
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground">
                  No customers currently excluded.
                </p>
              )}
            </div>
          </div>
        </div>

        <Separator />

        <div className="flex justify-end">
          <Button size="sm" onClick={() => onClose?.()}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};
